import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { useEffect, useLayoutEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import { hasConsent } from './utils/cookieConsent';
import { trackMetaRoute } from './utils/metaPixel';
import { useDocumentTheme } from './utils/theme';
import { usePageTitle } from './utils/pageTitle';
import { useAppOnPhone } from './utils/useAppOnPhone';
import { useConfirmedEmailNotice } from './utils/useConfirmedEmailNotice';

import { Home } from './pages/Home';
import { Dashboard } from './pages/Dashboard';
import { Workouts } from './pages/Workouts';
import { Workout } from './pages/Workout';
import { WorkoutExecution } from './pages/WorkoutExecution';
import { Progress } from './pages/Progress';
import { Tutorial } from './pages/Tutorial';
import { SplitReview } from './pages/SplitReview';
import { Plans } from './pages/Plans';
import { Account } from './pages/Account';
import { Academy } from './pages/Academy';
import { ExerciseHub } from './pages/ExerciseHub';
import { ResetPassword } from './pages/ResetPassword';
import { DietPlan } from './pages/DietPlan';
import { CalorieTracker } from './pages/CalorieTracker';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { NotFound } from './pages/NotFound';

import { Navbar } from './components/Navbar';
import { AppWelcome } from './components/auth/AppWelcome';
import { AuthDialogProvider } from './components/auth/AuthDialogProvider';
import { FloatingTabBar } from './components/app/FloatingTabBar';
import { AppHeader } from './components/app/AppHeader';
import { AppBanner } from './components/app/AppBanner';
import { EmailConfirmBanner } from './components/app/EmailConfirmBanner';
import { AppSession } from './components/app/AppSession';
import { CookieBanner } from './components/app/CookieBanner';
import { InstallAppProvider } from './components/app/InstallAppProvider';
import { InstallBanner } from './components/app/InstallBanner';
import { PushProvider } from './components/app/PushProvider';
import { Footer } from './components/Footer';
import { holdPageTop, restoreScroll, scrollTopOnNavigation } from './utils/pageTop';
import { refreshReads } from './utils/quietRefresh';
import { isTabNavigation, scrollOfEntry, scrollOfPath } from './utils/scrollMemory';
import { Notification } from './components/Notification';

const isSignedIn = () => !!localStorage.getItem('authToken');

const ScrollToTop = () => {
  const { pathname, key, state } = useLocation();
  const client = useApolloClient();

  // The Meta Pixel's PageView for each new page (it only runs after cookie consent)
  useEffect(() => {
    trackMetaRoute(pathname);
  }, [pathname]);

  const navigationType = useNavigationType();

  // A new page starts at its top, before it's painted, and keeps to it while its content settles in (data, banners,
  // images move things on some phones), until the person scrolls or presses anything. A page reached by back/forward
  // or by its tab goes back where it was left (utils/scrollMemory)
  useLayoutEffect(() => {
    const isBack = navigationType === 'POP';
    const place = isBack ? scrollOfEntry(key) : isTabNavigation(state) ? scrollOfPath(pathname) : null;
    if (place) restoreScroll(place);
    else window.scrollTo(0, 0);
    // Move focus to main content on route change for screen readers
    const main = document.getElementById('main-content');
    if (main) main.focus({ preventScroll: true });
    if (isBack || place) return undefined;
    return holdPageTop();
    // Only a change of page: a page's own steps (?e=, ?s=, ?w=) keep their scroll handling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // What the page shows is read again behind it, once its queries are listening
  useEffect(() => {
    const timer = setTimeout(() => refreshReads(client), 0);
    return () => clearTimeout(timer);
  }, [client, pathname]);

  return null;
};

const LoggetOutRouteWrapper = ({ children }) => {
  const { t } = useTranslation();
  useDocumentTheme();
  usePageTitle();
  // Inside the installed app on a phone these pages (the legal ones, from Conta) stay in the app: its header, its tab
  // bar for someone signed in, no footer
  const appOnPhone = useAppOnPhone();
  // The sign-in window (one for login, signup and password recovery) lives with the public pages
  return (
    <AuthDialogProvider>
      <Notification />
      <ScrollToTop />
      <InstallBanner />
      {appOnPhone ? <AppHeader /> : <Navbar />}
      <div role="main" aria-label={t('standalone.mainContent')}>
        {children}
      </div>
      <CookieBanner />
      {!appOnPhone && <Footer />}
      {appOnPhone && isSignedIn() && <FloatingTabBar railOnDesktop />}
    </AuthDialogProvider>
  );
};

// Signed out inside the installed app on a phone: the screen alone, with no header, tab bar or cookie card
const AppWelcomeWrapper = ({ children }) => {
  const { t } = useTranslation();
  useDocumentTheme();
  usePageTitle();
  return (
    <>
      <Notification />
      <ScrollToTop />
      <div role="main" aria-label={t('standalone.mainContent')}>
        {children}
      </div>
    </>
  );
};

const AppShell = ({ children }) => {
  const { t } = useTranslation();
  useDocumentTheme();
  usePageTitle();
  const { pathname } = useLocation();
  const appOnPhone = useAppOnPhone();
  return (
    <PushProvider busy={pathname.startsWith('/execute')}>
      <Notification />
      <ScrollToTop />
      <AppSession />
      {/* Android's sits fixed above the tab bar; the iPhone's here at the very top, above everything else */}
      <InstallBanner overTabBar />
      <EmailConfirmBanner />
      {/* A guided session is full screen, the set in thumb reach: the CMS announcement waits for the next page */}
      {!pathname.startsWith('/execute') && <AppBanner />}
      {/* Phones: the header on every app page but the full-screen guided session (desktop has the rail) */}
      {!pathname.startsWith('/execute') && <AppHeader />}
      <div role="main" aria-label={t('standalone.mainContent')}>
        {children}
      </div>
      {/* A guided session is a full-screen workout with its own controls at the bottom: no footer there. Nor in the
          installed app on a phone, whose doors are in Conta */}
      {!pathname.startsWith('/execute') && !appOnPhone && <Footer variant="app" />}
      <CookieBanner />
      {/* The navigation on every app page (the rail on desktop, the pill on phones) but the full-screen guided session.
          Pages with a back bar keep the pill too: the way to the rest of the app is never more than a tap away */}
      {!pathname.startsWith('/execute') && <FloatingTabBar railOnDesktop />}
    </PushProvider>
  );
};

/**
 * Every logged-in route goes through this one component, the home and /dashboard too, so moving between them never
 * rebuilds the shell: the tab bar has to live through it, or a shrunken bar snaps to full size and the capsule starts
 * over (owner report 30/10/2026). `isRoot`: `/`, which is the landing on the site and, inside the installed app on a
 * phone, the app's home signed in and its welcome signed out. `isDashboard`: /dashboard, which goes to `/` there: iPhones
 * keep the start address from install time, and links all over the app still say /dashboard.
 */
const RouteWrapper = ({ children, isRoot = false, isDashboard = false }) => {
  const { search } = useLocation(); // Signing in only changes the address: this reads the session again
  const navigate = useNavigate();
  const appOnPhone = useAppOnPhone();
  useConfirmedEmailNotice({
    enabled: isRoot && appOnPhone,
    onLogin: () => navigate('/?flow=login&step=1', { replace: true }),
  });

  if (isDashboard && appOnPhone) return <Navigate to={{ pathname: '/', search }} replace />;
  if (isRoot && !appOnPhone) {
    return (
      <LoggetOutRouteWrapper>
        <Home />
      </LoggetOutRouteWrapper>
    );
  }
  if (isRoot && !isSignedIn()) {
    return (
      <AppWelcomeWrapper>
        <AppWelcome />
      </AppWelcomeWrapper>
    );
  }
  // The signup ads' link, for someone already signed in, as on the landing
  if (isRoot && new URLSearchParams(search).get('cadastro') === '1') return <Navigate to="/workout" replace />;
  return <AppShell>{children}</AppShell>;
};

const LoginRoute = () => {
  const appOnPhone = useAppOnPhone();
  useLocation(); // A sign-in here goes on to the page it came from, or this reads the session again
  if (!appOnPhone) {
    return (
      <LoggetOutRouteWrapper>
        <Home />
      </LoggetOutRouteWrapper>
    );
  }
  if (isSignedIn()) return <Navigate to="/" replace />;
  return (
    <AppWelcomeWrapper>
      <AppWelcome />
    </AppWelcomeWrapper>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RouteWrapper isRoot>
        <Dashboard />
      </RouteWrapper>
    ),
  },
  {
    path: '/login/*',
    element: <LoginRoute />,
  },
  {
    path: '/reset-password',
    element: (
      <LoggetOutRouteWrapper>
        <ResetPassword />
      </LoggetOutRouteWrapper>
    ),
  },
  {
    path: '/terms',
    element: (
      <LoggetOutRouteWrapper>
        <Terms />
      </LoggetOutRouteWrapper>
    ),
  },
  {
    path: '/privacy',
    element: (
      <LoggetOutRouteWrapper>
        <Privacy />
      </LoggetOutRouteWrapper>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <RouteWrapper isDashboard>
        <Dashboard />
      </RouteWrapper>
    ),
  },
  {
    path: '/workouts',
    element: (
      <RouteWrapper>
        <Workouts />
      </RouteWrapper>
    ),
  },
  {
    path: '/workout/:workoutId?',
    element: (
      <RouteWrapper>
        <Workout />
      </RouteWrapper>
    ),
  },
  {
    path: '/execute/:workoutId',
    element: (
      <RouteWrapper>
        <WorkoutExecution />
      </RouteWrapper>
    ),
  },
  {
    path: '/progress',
    element: (
      <RouteWrapper>
        <Progress />
      </RouteWrapper>
    ),
  },
  {
    path: '/split-review',
    element: (
      <RouteWrapper>
        <SplitReview />
      </RouteWrapper>
    ),
  },
  {
    path: '/sync',
    element: (
      <RouteWrapper>
        <Tutorial />
      </RouteWrapper>
    ),
  },
  {
    path: '/plans',
    element: (
      <RouteWrapper>
        <Plans />
      </RouteWrapper>
    ),
  },
  {
    path: '/academy',
    element: (
      <RouteWrapper>
        <Academy />
      </RouteWrapper>
    ),
  },
  {
    path: '/exercises',
    element: (
      <RouteWrapper>
        <ExerciseHub />
      </RouteWrapper>
    ),
  },
  {
    path: '/diet',
    element: (
      <RouteWrapper>
        <DietPlan />
      </RouteWrapper>
    ),
  },
  {
    path: '/calorie-tracker',
    element: (
      <RouteWrapper>
        <CalorieTracker />
      </RouteWrapper>
    ),
  },
  {
    path: '/account',
    element: (
      <RouteWrapper>
        <Account />
      </RouteWrapper>
    ),
  },
  // Anything else: the server answers it with a 404 too (meu-treino-api src/seo/pages.js)
  {
    path: '*',
    element: (
      <LoggetOutRouteWrapper>
        <NotFound />
      </LoggetOutRouteWrapper>
    ),
  },
]);

// Each new page goes to the top before it's drawn (Safari: utils/pageTop.js)
scrollTopOnNavigation(router);

function App() {
  const isLocalhost = window.location.hostname === 'localhost';
  const consentGiven = hasConsent();

  return (
    <>
      {/* No <title> here: usePageTitle (in both route wrappers) owns it, and a Helmet title would race it */}
      <Helmet>
        {consentGiven && isLocalhost && (
          <script type="text/javascript">
            {`window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};
            var r=document.createElement("script");
            r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";
            var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);
            for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
            heap.load("3307113423",{replayEnabled:false});`}
          </script>
        )}
        {consentGiven && !isLocalhost && (
          <script type="text/javascript">
            {`window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};
            var r=document.createElement("script");
            r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";
            var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);
            for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
            heap.load("1715548186",{replayEnabled:false});`}
          </script>
        )}
      </Helmet>
      <InstallAppProvider>
        <RouterProvider router={router} />
      </InstallAppProvider>
    </>
  );
}

export default App;
